/**
 * LocalStorage utility functions with fallback
 */

// Cache the write-test result: localStorage availability doesn't change over a
// page's lifetime, so re-running the probe on every get/set is wasted work.
let storageAvailable: boolean | null = null;

const isStorageAvailable = (): boolean => {
    if (storageAvailable !== null) {
        return storageAvailable;
    }

    try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        storageAvailable = true;
    } catch {
        storageAvailable = false;
    }

    return storageAvailable;
};

const memoryStorage: { [key: string]: any } = {};

export const saveToStorage = (key: string, value: any): void => {
    try {
        if (isStorageAvailable()) {
            localStorage.setItem(key, JSON.stringify(value));
        } else {
            memoryStorage[key] = value;
        }
    } catch (err) {
        console.warn('PulseRelease: Failed to save to storage', err);
        memoryStorage[key] = value;
    }
};

export const getFromStorage = (key: string): any => {
    try {
        if (isStorageAvailable()) {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } else {
            return memoryStorage[key] || null;
        }
    } catch (err) {
        console.warn('PulseRelease: Failed to read from storage', err);
        return memoryStorage[key] || null;
    }
};

export const removeFromStorage = (key: string): void => {
    try {
        if (isStorageAvailable()) {
            localStorage.removeItem(key);
        }
        delete memoryStorage[key];
    } catch (err) {
        console.warn('PulseRelease: Failed to remove from storage', err);
    }
};

/**
 * Short, stable, non-cryptographic hash of a string, rendered in base36. Used to
 * namespace storage keys per API key so two apps sharing an origin (e.g. a
 * staging and a production build) don't collide on the same session token,
 * seen-set or anonymous id. Collision resistance isn't a security property here
 * — it only needs to keep distinct API keys in distinct buckets.
 */
export const hashKey = (input: string): string => {
    // FNV-1a (32-bit).
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
};

/**
 * Generate a cryptographically random anonymous identifier.
 *
 * Prefers `crypto.randomUUID()` (modern browsers / Node 16+). Falls back to
 * `crypto.getRandomValues()` formatted as a UUID v4 for older browsers, and
 * as a last resort to `Math.random()` so we never throw in exotic
 * environments — the caller relies on this returning a string.
 */
export const generateAnonymousId = (): string => {
    const cryptoObj: Crypto | undefined =
        typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;

    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
        return `anon_${cryptoObj.randomUUID()}`;
    }

    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        cryptoObj.getRandomValues(bytes);

        // RFC 4122 v4: set version (0100) and variant (10xx) bits.
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;

        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `anon_${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    // Final fallback — cryptographically weak, but better than throwing.
    return `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};
